import { useCallback, useEffect, useState } from "react";
import { Card, Label, MonoMeta, StateWord } from "@/design/primitives";
import { cn } from "@/lib/utils";

// ── ReviewBay (Phase 8 C) ─────────────────────────────────────────────────────
// The operator surface for executor outputs. It polls the awaiting_review lane,
// lets Jeff accept, revise-with-note, reject, or cancel per task — no modal,
// one click, immediate. After an action the lane refreshes. An empty lane says
// so in one calm line.
//
// Phase 8 C: when expanded, the generated text from /output is shown front and
// centre; meta (model, chars, duration, sha8) sits in a mono subtext line.
// When the output endpoint has no stored text, today's detail behaviour is kept.

const POLL_MS = 15000;

function useReviewTasks() {
  const [tasks, setTasks] = useState(null); // null = not loaded yet
  const [error, setError] = useState(null);

  const fetchList = useCallback(async () => {
    try {
      const res = await fetch("/api/exec/tasks?state=awaiting_review");
      if (!res.ok) throw new Error("Board unreachable");
      const body = await res.json();
      setTasks(Array.isArray(body) ? body : body.items || []);
      setError(null);
    } catch (e) {
      setError(e.message);
    }
  }, []);

  useEffect(() => {
    fetchList();
    const t = setInterval(fetchList, POLL_MS);
    return () => clearInterval(t);
  }, [fetchList]);

  return { tasks, error, refresh: fetchList };
}

function useTaskDetail(taskId) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!taskId) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/exec/tasks/${encodeURIComponent(taskId)}`);
      if (!res.ok) throw new Error("Detail unreachable");
      const body = await res.json();
      setDetail(body);
    } catch {
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  return { detail, loading, load };
}

function useTaskOutput(taskId) {
  const [output, setOutput] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!taskId) return;
    setLoading(true);
    try {
      const res = await fetch(
        `/api/exec/tasks/${encodeURIComponent(taskId)}/output`,
      );
      if (!res.ok) throw new Error("Output unreachable");
      const body = await res.json();
      setOutput(body);
    } catch {
      setOutput(null);
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  return { output, loading, load };
}

const humanizeState = (s) => {
  const map = {
    accepted: "Accepted",
    revision_requested: "Redo requested",
    failed: "Failed",
    cancelled: "Cancelled",
    awaiting_review: "Awaiting review",
  };
  return map[s] || s;
};

const toneForState = (s) => {
  const map = {
    accepted: "green",
    revision_requested: "amber",
    failed: "red",
    cancelled: "muted",
    awaiting_review: "amber",
  };
  return map[s] || "muted";
};

const ActionButton = ({ label, tone, busy, onClick }) => {
  const toneCls =
    tone === "green"
      ? "border-os-green text-os-green hover:bg-os-green/10"
      : tone === "red"
        ? "border-os-red text-os-red hover:bg-os-red/10"
        : tone === "amber"
          ? "border-os-amber text-os-amber hover:bg-os-amber/10"
          : "border-os-border text-os-ink hover:border-os-border-strong";
  return (
    <button
      type="button"
      disabled={busy}
      onClick={onClick}
      className={cn(
        "rounded-control border px-2.5 py-1 font-mono text-[0.6875rem] transition-colors disabled:opacity-40",
        toneCls,
      )}
    >
      {busy ? "Working…" : label}
    </button>
  );
};

const ReviewRow = ({ task, onAction, busy }) => {
  const [expanded, setExpanded] = useState(false);
  const [showRedoNote, setShowRedoNote] = useState(false);
  const [redoNote, setRedoNote] = useState("");
  const { detail, loading: detailLoading, load: loadDetail } = useTaskDetail(
    expanded ? task.id : null,
  );
  const { output, load: loadOutput } = useTaskOutput(
    expanded ? task.id : null,
  );

  useEffect(() => {
    if (expanded) {
      loadDetail();
      loadOutput();
    }
  }, [expanded, loadDetail, loadOutput]);

  const latest =
    detail?.attempts && detail.attempts.length > 0
      ? detail.attempts[detail.attempts.length - 1]
      : null;
  const result = latest?.result_json || null;
  const errorText = latest?.error_text || "";
  const cost = latest?.cost_usd ?? null;

  // Duration from created_at string to now in seconds
  const createdAt = task.created_at ? new Date(task.created_at) : null;
  const durationSec = createdAt
    ? Math.max(0, Math.floor((Date.now() - createdAt.getTime()) / 1000))
    : null;
  const durationText =
    durationSec === null
      ? ""
      : durationSec < 60
        ? `${durationSec}s`
        : durationSec < 3600
          ? `${Math.floor(durationSec / 60)}m`
          : `${Math.floor(durationSec / 3600)}h ${Math.floor((durationSec % 3600) / 60)}m`;

  const onRedo = async () => {
    const note = redoNote.trim();
    if (!note) return;
    const ok = await onAction("revision", task.id, note);
    if (ok) {
      setShowRedoNote(false);
      setRedoNote("");
    }
  };

  const outputMeta = output?.has_output
    ? [
        output.model || null,
        typeof output.output_chars === "number"
          ? `${output.output_chars} chars`
          : null,
        typeof output.duration_s === "number"
          ? `${output.duration_s.toFixed(1)}s`
          : null,
        output.output_sha8 || null,
      ].filter(Boolean)
    : [];

  return (
    <div
      className="flex flex-col gap-2 border-t border-os-border py-3 first:border-t-0"
      data-testid={`review-row-${task.id}`}
    >
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <StateWord tone={toneForState(task.state)}>
          {humanizeState(task.state)}
        </StateWord>
        <p className="min-w-0 flex-1 truncate text-[0.9375rem] font-medium text-os-ink">
          {task.title || task.id}
        </p>
        <MonoMeta className="shrink-0">{task.category || ""}</MonoMeta>
        {durationText && (
          <MonoMeta className="shrink-0">{durationText}</MonoMeta>
        )}
        {cost !== null && (
          <MonoMeta className="shrink-0">${cost.toFixed(4)}</MonoMeta>
        )}
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="shrink-0 font-mono text-[0.6875rem] text-os-ink-3 underline-offset-4 hover:text-os-ink hover:underline"
        >
          {expanded ? "Hide" : "Show"}
        </button>
      </div>

      {expanded && (
        <div className="flex flex-col gap-2 rounded-card border border-os-border bg-os-wash px-4 py-3">
          {/* Primary content: generated text from the /output endpoint */}
          {output?.has_output && (
            <div className="flex flex-col gap-2">
              <p
                className="whitespace-pre-wrap text-[0.9375rem] leading-[1.6] text-os-ink"
                data-testid="review-output-text"
              >
                {output.output_text}
              </p>
              {outputMeta.length > 0 && (
                <MonoMeta
                  className="text-os-ink-3"
                  data-testid="review-output-meta"
                >
                  {outputMeta.join(" · ")}
                </MonoMeta>
              )}
            </div>
          )}

          {/* Fallback: detail content when output is absent or has_output false */}
          {(!output || !output.has_output) && (
            <>
              {detailLoading && (
                <p className="text-[0.8125rem] text-os-ink-3">Loading…</p>
              )}
              {!detailLoading && result && (
                <div className="flex flex-col gap-1.5">
                  <p className="whitespace-pre-wrap text-[0.875rem] leading-[1.5] text-os-ink">
                    {typeof result === "string"
                      ? result
                      : result.output || JSON.stringify(result)}
                  </p>
                  {typeof result === "object" && result !== null && (
                    <MonoMeta className="mt-1 whitespace-pre-wrap">
                      {JSON.stringify(result, null, 2)}
                    </MonoMeta>
                  )}
                </div>
              )}
              {!detailLoading && errorText && (
                <p className="whitespace-pre-wrap rounded-control bg-os-red/10 px-3 py-2 text-[0.8125rem] text-os-red">
                  {errorText}
                </p>
              )}
            </>
          )}

          <div className="mt-1 flex flex-wrap items-center gap-2">
            <ActionButton
              label="Accept"
              tone="green"
              busy={busy}
              onClick={() => onAction("accept", task.id)}
            />
            <ActionButton
              label="Ask for redo"
              tone="amber"
              busy={busy}
              onClick={() => setShowRedoNote((v) => !v)}
            />
            <ActionButton
              label="Reject"
              tone="red"
              busy={busy}
              onClick={() => onAction("reject", task.id)}
            />
            <ActionButton
              label="Cancel"
              tone="muted"
              busy={busy}
              onClick={() => onAction("cancel", task.id)}
            />
          </div>

          {showRedoNote && (
            <div className="mt-1 flex items-center gap-2">
              <input
                type="text"
                value={redoNote}
                onChange={(e) => setRedoNote(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") onRedo();
                  if (e.key === "Escape") {
                    setShowRedoNote(false);
                    setRedoNote("");
                  }
                }}
                placeholder="One line — what needs fixing"
                className="min-w-0 flex-1 rounded-control border border-os-border bg-white px-3 py-1.5 text-[0.8125rem] text-os-ink outline-none placeholder:text-os-ink-3 focus:border-os-border-strong"
                data-testid="review-redo-input"
              />
              <ActionButton
                label="Send redo"
                tone="amber"
                busy={busy}
                onClick={onRedo}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const ReviewBay = ({ blankSlate = false }) => {
  const { tasks, error, refresh } = useReviewTasks();
  const [actingId, setActingId] = useState(null);

  const handleAction = useCallback(
    async (action, taskId, note) => {
      setActingId(taskId);
      try {
        const res = await fetch(
          `/api/exec/tasks/${encodeURIComponent(taskId)}/review`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ action, ...(note ? { note } : {}) }),
          },
        );
        if (!res.ok) throw new Error("Review action failed");
        await refresh();
        return true;
      } catch {
        return false;
      } finally {
        setActingId(null);
      }
    },
    [refresh],
  );

  // Jeff 10/30 blank slate: DeskPage passes blankSlate while testing data is out —
// the section keeps its header and reads the honest empty line.
const taskArray = blankSlate ? [] : tasks || [];

  return (
    <section data-testid="review-bay" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <Label as="h2">Awaiting review</Label>
        <MonoMeta as="p" data-testid="review-bay-count">
          {taskArray.length} task{taskArray.length === 1 ? "" : "s"} waiting
        </MonoMeta>
      </div>
      {error && (
        <Card quiet className="px-5 py-4">
          <p className="text-[0.875rem] text-os-red">{error}</p>
        </Card>
      )}
      {blankSlate || taskArray.length === 0 || error ? (
        <Card quiet className="px-5 py-4">
          <p className="text-[0.875rem] text-os-ink-2" data-testid="review-empty">
            Nothing waiting for review
          </p>
        </Card>
      ) : (
        <Card className="px-5 py-3">
          {taskArray.map((task) => (
            <ReviewRow
              key={task.id}
              task={task}
              onAction={handleAction}
              busy={actingId === task.id}
            />
          ))}
        </Card>
      )}
    </section>
  );
};

export default ReviewBay;
